import { onBeforeUnmount, reactive, ref } from 'vue'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router'
import { debounce } from 'lodash'

import { events$, $loading } from '@/services'
import { useBasket } from '@/hooks'
import useLogger from '@/hooks/useLogger'
import { ReactivityUtil } from '@/utils/Reactivity'

import { EVENT_DAP_SAVING, EVENT_CANCEL_DEBOUNCED_SAVING, EVENT_FLUSH_DEBOUNCED_SAVING } from '@/config/events'

const DEFAULT_DEBOUNCE = 15000

// data
const savedBatch = reactive({})
let personId = ref(undefined)

//hooks
const { updateBasketForPerson, updateOvpBasket } = useBasket()
const { logError } = useLogger()

let debouncedSave = _savedBatch => {
  logError('No method registered to debouncedSave method')
}

// methods

function cancelSave() {
  debouncedSave.cancel()
}

function flushSave() {
  debouncedSave.flush()
}

function save(data) {
  if (Object.keys(data).length === 0) {
    return Promise.resolve()
  }
  debouncedSave.cancel()

  $loading.start()

  events$.emit(EVENT_DAP_SAVING)

  return saveBasket(data)
    .then(basket => {
      return updateOvpBasket(basket)
    })
    .then(basket => {
      ReactivityUtil.resetData(savedBatch)
      return basket
    })
    .finally(() => $loading.end())
}

function saveBasket(data) {
  return personId.value ? updateBasketForPerson(personId.value, data) : Promise.resolve(data)
}

function saveBeforeRouting(next) {
  const promise = save(savedBatch)
  if (typeof promise?.then === 'function') {
    promise.then(() => next())
  } else {
    next()
  }
}

function store(sectionId, formData) {
  savedBatch[sectionId] = formData

  debouncedSave(savedBatch)
}

function setPersonId(__personId) {
  personId.value = __personId
}

// @TODO we will need to change later on also considering the logic in healthquestions
export default function useDebouncedSaving() {
  debouncedSave = debounce(() => save(savedBatch), DEFAULT_DEBOUNCE)

  events$.on(EVENT_CANCEL_DEBOUNCED_SAVING, cancelSave)
  events$.on(EVENT_FLUSH_DEBOUNCED_SAVING, flushSave)

  // lifecycle hooks
  onBeforeUnmount(() => {
    //TODO maybe reassigning the function doesn't make sense here, as it's exposed.
    // check if calling cancel here makes sense with the router hooks, onBeforeUnmount should be called last
    // so it should work
    debouncedSave = _savedBatch => {
      logError('No method registered to debouncedSave method')
    }

    personId.value = null

    events$.off(EVENT_CANCEL_DEBOUNCED_SAVING, cancelSave)
    events$.off(EVENT_FLUSH_DEBOUNCED_SAVING, flushSave)

    ReactivityUtil.resetData(savedBatch)
  })

  // router hooks
  onBeforeRouteLeave((_to, _from, next) => {
    saveBeforeRouting(next)
  })

  onBeforeRouteUpdate((_to, _from, next) => {
    saveBeforeRouting(next)
  })

  return {
    //data
    savedBatch,
    //methods
    cancelSave,
    debouncedSave,
    flushSave,
    save,
    saveBasket,
    saveBeforeRouting,
    setPersonId,
    store,
  }
}
